/**
 * Echo Hunt -- the weaver prompt (P8, §8.1 + §8.2).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "The weaver".
 *  - knows real/fake?      NO. The bullets arrive SHUFFLED (the pool builder assigns random slot ids
 *                          after shuffling, §16.0), and this prompt is built from `{id, text}` pairs
 *                          only -- there is no parameter through which a class could be passed, and
 *                          nothing in the wording sets up a class distinction (§2 invariant #2).
 *  - sees world/character?  YES -- this is one of only two steps allowed to (invariant #4). The
 *                          asymmetry is safe here because the weaver treats every bullet the same way:
 *                          it rebridges all of them into the same world, so a real bullet is not
 *                          "adapted to" the world any more than a fake is.
 *  - sees other bullets?    YES, all of them, shuffled. This is the one step that must see them all --
 *                          ordering them into a continuous story is the job (§8.1).
 *  - sees corpus?           NO.
 *
 * ## The frame (§8.2)
 *
 * The character is in what is effectively a lucid-capable dream -- powers, doors that lead anywhere,
 * remote worlds -- and BELIEVES THEY ARE AWAKE. The weaver is told the truth so that the character's
 * in-story rationalizations of impossible events are written knowingly: each of those rationalizations
 * models the exact failure the game trains against (§8.2 dramatic irony).
 *
 * The truth is stated PLAINLY TO THE READER by the Play tab (§8.2 `[LIKELY]`), not by this prompt: the
 * installment is a list of events, so an in-text statement would have to live in narration that does
 * not exist yet. Keeping it out of the bullets also keeps them the same shape as every other bullet in
 * the pipeline (invariant #3).
 *
 * ## Two classes of rule, kept apart on purpose
 *
 *  - CONTENT rules (the bullets are already-good events; the weaver orders and bridges them, and may
 *    not invent new events) -- this is what stops the weaver from rewriting the round into one story
 *    that no longer contains the events being judged (§17 "real bullets eroded by weave").
 *  - SURFACE rules (house style, §4.3/§7) -- re-stated here because a bridging sentence is new text
 *    and would otherwise reintroduce the user's `--` dash habit into the middle of a woven bullet.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.1, §8.2, §16.0.
 */
import type {JSONSchemaLike} from "../LLM.js";
import {HOUSE_STYLE_RULES} from "./IndexPrompt.js";

/** What the weaver must do with the material, in order of importance (§8.1). */
export const WEAVE_RULES = [
	"Put the bullets in an order that reads as ONE continuous stretch of the same person's experience.",
	"Rebridge each bullet: you may reword it and add connecting detail at its edges so it joins its neighbours.",
	"Keep the EVENT inside every bullet exactly as it is -- the same action, the same objects, the same place, the same people. You are joining events, not replacing them.",
	"Do NOT add new events, new characters, new places or new turns of the plot. If a bullet needs a cause or a consequence, use something already present in the story, the world or the character.",
	"Spread the connecting edits across ALL the bullets. Do not pick a few bullets to carry the whole bridge while the rest stay untouched -- an uneven edit pattern is itself noticeable.",
	"Do not resolve the story: no epiphany, no moral, no tidy ending, and no explanation of why anything happened.",
	"The character believes they are awake. Their reasoning about impossible events is ordinary reasoning, applied badly to the situation -- they are not puzzled by the world in general, only, occasionally, by something specific.",
	"Do not mention or hint at any of this: no word about dreams, sleep, waking, awareness, reality or being watched. The character has none of that information.",
].map(a=>`- ${a}`).join("\n");

/** The note added to the rules list when `Weave_UserPromptOptions.includeParaphraseNote` is set (the Settings
 * -> Weaver checkbox, `echoProfiles.weaveParaphraseNote`, off by default).
 *
 * The paraphrase itself is the reshape step's job (`Prompts/Reshape.ts`), so this is an experiment rather than
 * something load-bearing: it may break up wording the reshape left close to the original, at some risk of the
 * weaver drifting from the events (there is no retention check on woven text). */
export const WEAVE_PARAPHRASE_NOTE = "You are free to reword each bullet entirely -- different words, different sentence structure -- as long as its event stays the same. Prefer your own phrasing over the bullet's where the two would read equally well.";

/** The rules list as actually sent, with the optional paraphrase note appended. */
export function WeaveRulesText(includeParaphraseNote = false): string {
	return includeParaphraseNote ? `${WEAVE_RULES}\n- ${WEAVE_PARAPHRASE_NOTE}` : WEAVE_RULES;
}

/** The `summaryOut` shape (§8.1 "a short steady-length storyline summary"). Steady length matters:
 * the summary is fed back into the NEXT round, so a summary that grows or shrinks round by round
 * slowly changes how much the weaver knows about the past. */
export const WEAVE_SUMMARY_RULES = [
	"Write 3 to 6 sentences, always roughly the same length.",
	"Record what the character did and where they now are, in order.",
	"Keep it concrete: names, places, what happened to whom. No themes, no interpretation, no mood.",
	"Write it as a record of facts, not as a story: no hooks, no cliffhanger phrasing.",
].map(a=>`- ${a}`).join("\n");

export function Weave_SystemPrompt(): string {
	return [
		"You weave a set of unrelated remembered events into one continuous installment of an ongoing story.",
		"You keep every event intact and only join them.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type Weave_UserPromptOptions = {
	/** the frame id (`FRAME_DREAMING_BUT_AWAKE`); only its human description is written into the prompt */
	frame: string;
	worldName: string;
	worldBible: string;
	/** how tolerant this world is of absurd events surviving without being smoothed over (§8.5) */
	worldCrazinessTolerance: number;
	characterName: string;
	characterCautionVsOpportunity: number;
	characterNotes: string;
	/** the storyline so far; empty/null on the first installment of a story */
	summary?: string|n;
	/** user-set in-dream schematic rules the weave must uphold (§8.1, §8.3) */
	schematicRules?: string[]|n;
	/** P11/§11.5: the user asked for a way out this round, so one must appear */
	requestDoor?: boolean|n;
	/** §11.5 W3: the world the door this round offers should lead to. Its name is given to the model so the
	 * door can describe the far side honestly -- the target is fixed BEFORE the description, which is what
	 * stops a door "onto the Disc" from resolving into somewhere else. Absent = the pool has nowhere else
	 * to go, so no offer should be made. */
	doorTargetName?: string|n;
	/** §11.5 W3: the target world's bible, so the far side reads as that place rather than as a generic
	 * doorway. Only ever sent when the model is already being told to write a door. */
	doorTargetBible?: string|n;
	/** §11.5 W2: a door the PLAYER already accepted, now present in the world. The character decides
	 * whether to use it, which is the whole point of separating the two decisions. */
	stagedDoorDescription?: string|n;	/** §11.5 W4: the story moved since the previous installment, and this is the arrival. Derived from
	 * `worldHistory`, emitted for exactly one round. */
	worldChange?: {
		fromWorldName?: string|n;
		cause?: string|n;
		doorDescription?: string|n;
	}|n;
	/** ALREADY SHUFFLED. `{id, text}` only -- there is no field for provenance (§16.0). */
	bullets: {id: string, text: string}[];
	/** add the "you may reword freely" rule (Settings -> Weaver, off by default; 2026-10-21) */
	includeParaphraseNote?: boolean|n;
	/** on a retry: the deterministic problems found in the previous reply */
	fixNotes?: string|n;
};

/** Builds the weaver prompt. Every bullet is rendered as a `{"id": ..., "text": ...}` pair, so the
 * model has one unambiguous handle per bullet to return, and `Weave.ts` can check completeness by id
 * rather than by position (§8.1 "output bullet i corresponds to input bullet i"). */
export function Weave_UserPrompt(opts: Weave_UserPromptOptions): string {
	const {
		frame, worldName, worldBible, worldCrazinessTolerance,
		characterName, characterCautionVsOpportunity, characterNotes,
		summary, schematicRules, requestDoor, bullets, fixNotes, includeParaphraseNote,
		doorTargetName, doorTargetBible, stagedDoorDescription, worldChange,
	} = opts;

	const parts = [
		"## The frame",
		"Someone is having an experience that is, in fact, a dream, and they believe they are awake.",
		"They have whatever abilities this world allows them -- including, sometimes, powers, and doors that lead to other worlds.",
		"They act on their own ordinary values and personality, applied to an extraordinary situation.",
		"You know the truth; they do not. Write their actions and their reasoning as if the situation were real to them.",
		`Frame: ${frame}`,
		"",
		"## The world",
		`Name: ${worldName}`,
		worldBible.trim(),
		`How well this world tolerates bizarre events: ${DescribeCrazinessTolerance(worldCrazinessTolerance)}.`,
		"",
		"## The character",
		`Name: ${characterName}`,
		`Disposition: ${DescribeCautionVsOpportunity(characterCautionVsOpportunity)}.`,
		characterNotes.trim(),
		"",
	];

	if (summary != null && summary.trim().length > 0) {
		parts.push(
			"## The story so far",
			summary.trim(),
			"",
			"Continue from where that leaves the character. Do not repeat or re-explain it.",
			"",
		);
	} else {
		parts.push(
			"## The story so far",
			"This is the FIRST installment. The character is somewhere in this world, doing something ordinary, and the events below happen to them.",
			"",
		);
	}

	if ((schematicRules ?? []).length > 0) {
		parts.push(
			"## Rules of this world you must uphold",
			"These are hard constraints. Never write an event that contradicts them, and never use a shortcut they rule out.",
			...(schematicRules ?? []).map(rule=>`- ${rule}`),
			"",
		);
	}

	// §11.5 W4: emitted for exactly ONE installment after a move, then derived-away. The old world is named
	// but its bible is deliberately NOT supplied: the weaver is writing an exit from a place it no longer has
	// to model, and the less of it in the prompt the less chance of it bleeding into the new one.
	if (worldChange != null) {
		const from = worldChange.fromWorldName?.trim() ?? "";
		const doorText = worldChange.doorDescription?.trim() ?? "";
		const causeLine = worldChange.cause == "recovery"
			? "The place they were in stopped existing while they were in it, and they have come through into this one. They did not choose this."
			: worldChange.cause == "manual"
				? "They have left where they were and arrived here."
				: "They went through the way out described earlier and have come out here.";

		parts.push(
			"## Since the last installment",
			from.length > 0
				? `The story so far above ends in ${from}. Since then they have crossed into somewhere else, and this installment is their first stretch in the new place.`
				: "The story so far above ends somewhere else. Since then they have crossed into the place described above, and this installment is their first stretch here.",
			causeLine,
			...(doorText.length > 0 ? [`The way through was: ${doorText}`] : []),
			"Open the installment with the arrival, in two or three sentences at most, then carry on with the events below. Do NOT write them back in the previous place, and do not have them travel again in this installment.",
			"",
		);
	}

	parts.push(
		"## The events, in the order they should roughly happen",
		"Every one of these must appear in the installment. Their order here is a suggestion, not a rule -- move them if the story reads better, but do not drop any.",
		JSON.stringify(bullets.map(bullet=>({id: bullet.id, text: bullet.text})), null, 2),
		"",
		"## What to do",
		WeaveRulesText(includeParaphraseNote ?? false),
		"",
		"## House style",
		HOUSE_STYLE_RULES,
		"",
		"## The storyline summary for the next installment",
		WEAVE_SUMMARY_RULES,
		"",
	);

	// §11.5 W2/W3: three actors, three questions. What this prompt asks for depends on WHICH of them the
	// story is waiting on, and never on both at once.
	if (stagedDoorDescription != null && stagedDoorDescription.trim().length > 0) {
		// the player accepted a door last round: it is now a thing IN the world, and the character decides
		const doorText = stagedDoorDescription.trim();
		parts.push(
			"## The way out that is already there",
			`A way out is present in this world, and they can see it: ${doorText}`,
			"They already knew about it and they have had time to think about it. Decide, from this character's disposition and notes, whether they use it in this installment or leave it alone -- and write their reasoning in their own terms, not as a plot decision.",
			"If they use it, this installment ENDS there: describe them going through, and stop. Do NOT write what is on the other side -- the next installment opens there.",
			"Do not add a second way out this round. Return `null` for `doorOffer`.",
			"",
		);
	} else if (doorTargetName != null && doorTargetName.trim().length > 0) {
		// the weaver may propose one, and the target is already fixed so the far side can be described
		const targetName = doorTargetName.trim();
		parts.push(
			"## The optional door",
			requestDoor == true
				? "This installment MUST end with a way out: a door, a threshold, or a route that leads somewhere else. Describe it in one sentence, in this world's own terms, as something the character can see and could use."
				: "If the installment naturally ends with the character able to leave -- a door, a threshold, a way out -- describe it in one sentence, in this world's own terms.",
			`If there is one, it leads to ${targetName}. Describe only what they can see of it from HERE -- the opening, the light, the sound, the smell, a glimpse -- enough that ${targetName} is recognisable as what lies beyond. Do not write them arriving there.`,
			...(doorTargetBible != null && doorTargetBible.trim().length > 0
				? [`What lies beyond, for your reference only:`, doorTargetBible.trim()]
				: []),
			"Do NOT have them go through it in this installment, and do NOT move them somewhere else yourself. Whether to use it is theirs to decide later, and it is not decided on this page.",
			requestDoor == true
				? "A way out is required this round, so return it rather than null."
				: "Otherwise return `null` for it. Do not force one.",
			"",
		);
	} else {
		// nowhere else to go: an offer would be a lie, since its target could not be honoured
		parts.push(
			"## The optional door",
			"There is nowhere else for them to go from here. Return `null` for `doorOffer`, and do not write any way out of this world.",
			"",
		);
	}

	parts.push(
		"## Output",
		"Reply with ONE JSON object:",
		`{"order": ["<id>", "<id>", ...], "bullets": [{"id": "<id>", "text": "..."}, ...], "doorOffer": {"description": "..."} | null, "doorDecision": "taken" | "declined" | null, "summaryOut": "..."}`,
		"",
		"Hard requirements:",
		"- `order` contains EVERY id above, exactly once, in the order the story tells them.",
		"- `bullets` contains EVERY id above, exactly once, with that bullet rewritten so it joins the story.",
		"- Same number of bullets out as in. Never merge two into one, split one into two, add one, or drop one.",
		"- One sentence per bullet where possible. No headings, no bullet characters, no quotation marks.",
		stagedDoorDescription != null && stagedDoorDescription.trim().length > 0
			? "- `doorDecision` is REQUIRED this round: `taken` if they went through the way out, `declined` if they left it alone."
			: "- `doorDecision` is `null` this round -- there is no way out for them to decide about yet. Whether they use one is decided in a later installment.",
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix these problems from your previous attempt",
			fixNotes.trim(),
			"",
			"Reply with the FULL corrected object again (every id, not only the ones listed).",
		);
	}

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. Deliberately loose: completeness,
 * duplicates and the §7 gate are checked in `Weave.ts`, where a failure can be explained to the model
 * in plain language ("bullet 4 is missing") instead of as a schema error (§8.1). */
export function Weave_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["order", "bullets", "summaryOut"],
		properties: {
			order: {type: "array", minItems: 1, items: {type: "string"}},
			bullets: {
				type: "array",
				minItems: 1,
				items: {
					type: "object",
					required: ["id", "text"],
					properties: {
						id: {type: "string"},
						text: {type: "string"},
					},
				},
			},
			// `doorOffer` is deliberately NOT constrained here: it is either an object or null, and this
			// JSON spec has no union type. `ParseWeaveReply` in `Weave.ts` normalises both forms.
			summaryOut: {type: "string", minLength: 1},
		},
	};
}

/** Human-readable rendering of `crazinessTolerance` (0..1). A tolerant world is one where an absurd
 * event can simply happen; an intolerant one needs the absurdity to arrive through the world's own
 * rules, or the weave reads as a genre collision (§8.5). */
export function DescribeCrazinessTolerance(tolerance: number): string {
	if (!Number.isFinite(tolerance)) return "unknown";
	if (tolerance >= .9) return "almost anything can happen here and nobody in the story finds it remarkable";
	if (tolerance >= .75) return "very high -- strange events are ordinary local business";
	if (tolerance >= .5) return "moderate -- strange things happen, but they follow this world's own rules";
	if (tolerance >= .25) return "low -- only a few things here are impossible";
	return "very low -- this is nearly an ordinary world, so impossible events stand out in it";
}

/** Human-readable rendering of `values_cautionVsOpportunity` (-1 cautious .. +1 opportunistic). */
export function DescribeCautionVsOpportunity(value: number): string {
	if (!Number.isFinite(value)) return "unremarkable";
	if (value <= -.6) return "very cautious -- avoids risk, checks things, would rather leave than push";
	if (value <= -.2) return "cautious -- careful, slow to trust, unwilling to gamble";
	if (value < .2) return "neither cautious nor reckless";
	if (value < .6) return "opportunistic -- quick to try things, happy to exploit an opening";
	return "very opportunistic -- acts first, uses whatever is offered, rarely asks whether it should";
}
